import { ThemeProvider } from '@emotion/react';
import { Liff } from '@line/liff';
import { useRouter } from 'next/router';
import React, { useState, useEffect, useCallback, useRef } from 'react';

import { NotFound } from '@/components/partials/NotFound';
import { theme } from '@/styles/theme';

import { GlobalContext } from '../contexts/GlobalContext';

import type { Profile } from '@liff/get-profile';

interface LiffProviderProps {
  liffId?: string;
  children: React.ReactNode;
  onUserIdChange?: (_userId: string) => void;
}

export const LiffProvider = ({
  liffId,
  children,
  onUserIdChange,
}: LiffProviderProps) => {
  const [liffObject, setLiffObject] = useState<Liff | null>(null);
  const [liffError, setLiffError] = useState<string | null>(null);
  const [isNotFound, setIsNotFound] = useState<boolean>(false);
  const [liffProfile, setLiffProfile] = useState<Profile | null>(null);
  const previousUserId = useRef<string | null>(null);
  const router = useRouter();

  const initializeLiff = useCallback(
    async (liffId: string) => {
      try {
        if (liffObject) return;

        const liff = await import('@line/liff').then(module => module.default);
        await liff.init({ liffId });
        if (!liff.isLoggedIn()) {
          // 現在のURLをログイン後のリダイレクト先として設定
          const currentPath = router.asPath;
          const currentUrl = `${window.location.origin}${currentPath}`;
          liff.login({ redirectUri: currentUrl });
        }
        await liff.ready;

        setLiffObject(liff);

        await liff.getProfile().then(profile => {
          setLiffError(null);
          setLiffProfile(profile);
        });
      } catch (e) {
        console.warn(e);
        setLiffError('Failed to initialize LIFF');
        setIsNotFound(true);
      }
    },
    [liffObject, router]
  );

  // userIdが変更されたときにコールバックを呼び出す
  useEffect(() => {
    if (liffProfile?.userId && liffProfile.userId !== previousUserId.current) {
      previousUserId.current = liffProfile.userId;
      onUserIdChange?.(liffProfile.userId);
    }
  }, [liffProfile?.userId, onUserIdChange]);

  // ログイン状態の変更を監視し、適切なページにリダイレクト
  useEffect(() => {
    (async () => {
      if (liffObject && liffObject.isLoggedIn()) {
        // ログイン済みの場合、現在のパスが正しいかチェック
        const currentPath = router.asPath;

        // 動的ルート（[organizationId]など）の場合は置換を行わない
        const hasDynamicRoute =
          router.pathname.includes('[') && router.pathname.includes(']');

        if (!hasDynamicRoute) {
          const expectedPath = `/${liffId}${router.pathname === '/[liffId]' ? '' : router.pathname.replace('/[liffId]', '')}`;

          // パスが一致しない場合は適切なパスにリダイレクト
          if (currentPath !== expectedPath) {
            await router.replace(expectedPath);
          }
        }
      }
    })().catch(() => {
      // 何もしない
    });
  }, [liffObject, liffId, router]);

  useEffect(() => {
    (async () => {
      if (!liffId) {
        setLiffError('Liff ID is required');
        return;
      }

      await initializeLiff(liffId);
    })().catch(() => {
      // 何もしない
    });
  }, [initializeLiff, liffId, liffObject]);

  if (isNotFound) {
    return (
      <ThemeProvider theme={theme}>
        <NotFound />
      </ThemeProvider>
    );
  }

  if (!liffObject) {
    return null;
  }

  return (
    <GlobalContext.Provider
      value={{ liff: liffObject, liffProfile, liffError }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
